import { useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Modal, ConfirmDialog } from "../components/ui/Modal";
import { Input } from "../components/ui/Fields";
import { usePagedList } from "../hooks/usePagedList";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import type { Game } from "../api/types";

export function GamesPage() {
  const { rows, isLoading, error, page, setPage, hasNextPage, reload } = usePagedList<Game>("/admin/games");
  const [editing, setEditing] = useState<Partial<Game> | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Game | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  async function saveGame() {
    if (!editing?.name) {
      showToast("Name is required", "error");
      return;
    }
    setIsSubmitting(true);
    try {
      if (editing.id) {
        await apiRequest(`/admin/games/${editing.id}`, {
          method: "PATCH",
          body: { name: editing.name, thumbnail_url: editing.thumbnail_url, game_url: editing.game_url, is_active: editing.is_active },
        });
      } else {
        await apiRequest("/admin/games", {
          method: "POST",
          body: { name: editing.name, thumbnail_url: editing.thumbnail_url, game_url: editing.game_url, sort_order: 0, is_active: true },
        });
      }
      showToast("Saved", "success");
      reload();
      setEditing(null);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not save game", "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setIsSubmitting(true);
    try {
      await apiRequest(`/admin/games/${pendingDelete.id}`, { method: "DELETE" });
      showToast("Deleted", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not delete", "error");
    } finally {
      setIsSubmitting(false);
      setPendingDelete(null);
    }
  }

  const columns: Column<Game>[] = [
    {
      header: "Game",
      render: (row) => (
        <div className="flex items-center gap-3">
          {row.thumbnail_url ? <img src={row.thumbnail_url} className="h-9 w-9 rounded-lg object-cover" alt="" /> : <div className="h-9 w-9 rounded-lg bg-surface-raised" />}
          <span className="font-medium">{row.name}</span>
        </div>
      ),
    },
    { header: "Status", render: (row) => (row.is_active ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Inactive</Badge>) },
    {
      header: "",
      className: "text-right",
      render: (row) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={() => setEditing(row)}>
            Edit
          </Button>
          <Button size="sm" variant="danger" onClick={() => setPendingDelete(row)}>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AppShell title="Games">
      <div className="mb-4 flex justify-end">
        <Button onClick={() => setEditing({})}>+ Add Game</Button>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => r.id}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No games yet"
        pagination={{ page, pageSize: 20, hasNextPage, onPageChange: setPage }}
      />

      <Modal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Edit Game" : "Add Game"}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={saveGame} isLoading={isSubmitting}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input label="Name" value={editing?.name ?? ""} onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))} />
          <Input label="Thumbnail URL" value={editing?.thumbnail_url ?? ""} onChange={(e) => setEditing((p) => ({ ...p, thumbnail_url: e.target.value }))} />
          <Input label="Game URL" value={editing?.game_url ?? ""} onChange={(e) => setEditing((p) => ({ ...p, game_url: e.target.value }))} />
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        isLoading={isSubmitting}
        isDangerous
        title={`Delete "${pendingDelete?.name}"?`}
        description="This removes the game from the app for everyone."
        confirmLabel="Delete"
      />
    </AppShell>
  );
}
